import type { SoundName } from "./types.ts";

type AudioCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let humOsc: OscillatorNode | null = null;
let humGain: GainNode | null = null;

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const g = globalThis as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
    const Ctor = g.AudioContext ?? g.webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  } catch (e) {
    console.warn("AudioContext init failed:", e);
    return null;
  }
  return ctx;
}

export function beep(
  freq: number,
  durMs: number,
  type: OscillatorType = "square",
  volume = 0.15,
): void {
  const c = ensureCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const now = c.currentTime;
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(volume, now + 0.005);
  gain.gain.linearRampToValueAtTime(0, now + durMs / 1000);
  osc.connect(gain).connect(c.destination);
  osc.start(now);
  osc.stop(now + durMs / 1000 + 0.02);
}

export function playSound(name: SoundName): void {
  switch (name) {
    case "key_press":
      beep(660, 40);
      break;
    case "perfect":
      beep(880, 80);
      setTimeout(() => beep(1320, 80), 80);
      break;
    case "good":
      beep(660, 100);
      break;
    case "ok":
      beep(440, 100);
      break;
    case "too_early":
    case "too_late":
      beep(110, 250, "square", 0.2);
      break;
    case "game_over":
      beep(440, 200);
      setTimeout(() => beep(220, 200), 200);
      setTimeout(() => beep(110, 200), 400);
      break;
    case "peck":
      beep(1000, 30, "triangle", 0.1);
      break;
  }
}

export function startHum(): void {
  const c = ensureCtx();
  if (!c || humOsc) return;
  humOsc = c.createOscillator();
  humGain = c.createGain();
  const filter = c.createBiquadFilter();
  humOsc.type = "triangle";
  humOsc.frequency.value = 80;
  filter.type = "lowpass";
  filter.frequency.value = 120;
  humGain.gain.value = 0.03;
  humOsc.connect(filter).connect(humGain).connect(c.destination);
  humOsc.start();
}

export function stopHum(): void {
  if (!humOsc) return;
  try {
    humOsc.stop();
  } catch {
    // already stopped
  }
  humOsc.disconnect();
  humGain?.disconnect();
  humOsc = null;
  humGain = null;
}
